import React, { createContext, useContext, useState, useEffect } from "react";
import * as signalR from "@microsoft/signalr";
import { resourceAPIURL } from "../config";
import { IChatPresence } from "../models/Chat";

interface ActiveLeads {
  [leadId: string]: string[];
}

interface ChatPresenceMap {
  [userId: string]: IChatPresence;
}

interface SignalRContextType {
  connection: signalR.HubConnection | null;
  activeLeads: ActiveLeads;
  /** SignalR connection for the chat hub. Null until the initial start
   * resolves. Components subscribe directly via `connection.on(...)` and
   * should clean up their handlers on unmount. */
  chatConnection: signalR.HubConnection | null;
  /** Latest presence map keyed by userId, fed by `PresenceChanged` events. */
  chatPresence: ChatPresenceMap;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [activeLeads, setActiveLeads] = useState<ActiveLeads>({});
  const [chatConnection, setChatConnection] =
    useState<signalR.HubConnection | null>(null);
  const [chatPresence, setChatPresence] = useState<ChatPresenceMap>({});

  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${resourceAPIURL}/leadHub`, { withCredentials: false })
      .withAutomaticReconnect()
      .build();

    newConnection
      .start()
      .then(() => {
        setConnection(newConnection);
        console.log("Hub connected!");

        newConnection.on("InitialSync", (activeLeads: ActiveLeads) => {
          console.log("initial sync", activeLeads);
          setActiveLeads(activeLeads ?? {});
        });

        newConnection.on("RecordOpened", (leadId: string, userId: string) => {
          setActiveLeads((prev) => ({
            ...prev,
            [leadId]: prev[leadId] ? [...prev[leadId], userId] : [userId],
          }));
        });

        newConnection.on("RecordClosed", (leadId: string, userId: string) => {
          setActiveLeads((prev) => {
            const updatedUsers =
              prev[leadId]?.filter((id) => id !== userId) || [];

            if (updatedUsers.length === 0) {
              const { [leadId]: _, ...newState } = prev;
              return newState;
            }

            return {
              ...prev,
              [leadId]: updatedUsers,
            };
          });
        });
      })
      .catch((err) => console.error("SignalR connection error: ", err));

    return () => {
      newConnection.stop();
    };
  }, []);

  // Separate connection for the internal chat hub. Kept distinct from the
  // lead hub so a chat-side issue (eg. presence storm) can never blow up
  // the lead-watching feature, and so the chat page can subscribe to its
  // own events without filtering shared traffic.
  useEffect(() => {
    const newChatConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${resourceAPIURL}/chatHub`, { withCredentials: false })
      .withAutomaticReconnect()
      .build();

    newChatConnection.on("PresenceChanged", (userId: string, presence: IChatPresence) => {
      setChatPresence((prev) => ({ ...prev, [userId]: presence }));
    });

    newChatConnection
      .start()
      .then(() => {
        setChatConnection(newChatConnection);
        console.log("Chat hub connected!");
      })
      .catch((err) => console.error("Chat SignalR connection error: ", err));

    return () => {
      newChatConnection.stop();
    };
  }, []);

  return (
    <SignalRContext.Provider
      value={{ connection, activeLeads, chatConnection, chatPresence }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }

  return context;
};
